import api from "../services/api";
import i18n from "../i18n";
import TableViewICD10 from "../components/tableView/TableViewICD10";
import TableViewQCodes from "../components/tableView/TableViewQCodes";
import TableViewReviewed from "../components/tableView/TableViewReviewed";
import { confirmMissingCpt } from "../components/chatEdit/sections/qcodeMissingCpt";
import { stampNonBillable } from "./utils";

export const tableViewData = {
  color: {
    key: "contextVariables.local:color",
    type: "select",
    className: "no-underline table-icons",
    header: "",
    template: (value, chat, colData, extraData) => (
      <button className="white btn-round">
        <span className="material-symbols-outlined">colors</span>
      </button>
    ),

    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:color", value || "");
    },
    getOptions: () => {
      return colorOptions;
    },
    getValue: (chat) => {
      let value = chat.contextVariables?.["local:color"];
      if (!value) return "";
      let find = colorOptions.find((item) => item.key === value);
      if (find) return find.label;
    },
  },
  attachments: {
    header: i18n.t("tableView.columns.attachments"),
    className: "no-underline table-icons",
    template: (value, chat, colData, extraData) => (
      <button
        onClick={(e) => {
          extraData.setChat({ ...chat });
          extraData.setShowModalAttachments(true);
        }}
        className="white btn-round"
      >
        <span className="material-symbols-outlined">attach_file</span>
      </button>
    ),
  },
  hcfa1500: {
    header: i18n.t("tableView.columns.hcfa1500"),
    className: "no-underline table-icons",
    template: (value, chat, colData, extraData) => (
      <button
        onClick={(e) => {
          extraData.downloadPdf(chat);
        }}
        className="white btn-round"
      >
        <span className="material-symbols-outlined">docs</span>
      </button>
    ),
  },
  view: {
    header: i18n.t("tableView.columns.viewEdit"),
    className: "no-underline table-icons",
    template: (value, chat, colData, extraData) => (
      <button
        onClick={(e) => {
          extraData.setChat({ ...chat, ...{ isTableEdit: true } });
          extraData.setShowModalDetail(true);
        }}
        className="white btn-round"
      >
        <span className="material-symbols-outlined">visibility</span>
      </button>
    ),
  },
  edit: {
    header: i18n.t("tableView.columns.edit"),
    template: (value, chat, colData, extraData) => (
      <button
        onClick={(e) => {
          window.open("/chat/" + chat.id);
        }}
        className="white btn-round"
      >
        <span className="material-symbols-outlined">edit</span>
      </button>
    ),
  },
  reviewed: {
    header: i18n.t("tableView.columns.reviewed"),
    className: "table-icons",
    key: "contextVariables.local:reviewed",
    template: (value, chat, colData, extraData) => {
      return <TableViewReviewed chat={chat} value={value} colData={colData} extraData={extraData}></TableViewReviewed>;
    },
  },
  id: {
    key: "id",
    type: "text",
    header: i18n.t("tableView.columns.id"),
  },
  provider: {
    type: "select",
    header: i18n.t("tableView.columns.provider"),
    onEdit: async (chat, value, id, updateEncounter) => {
      let updatedChat = {
        chatId: chat.id,
        chatLastUpdate: new Date().getTime(),
        assignedTo: [value],
      };
      await api.updateChat(updatedChat);
    },
    getOptions: (extraData) => {
      return [
        {
          key: "",
          label: i18n.t("tableView.options.notAssigned"),
        },
        ...extraData.doctors
          .filter((item) => {
            return item.groups.indexOf("doctor") !== -1;
          })
          .map((item) => {
            return {
              key: item.id,
              label: item.lastName + ", " + item.firstName,
            };
          }),
      ];
    },
    getValue: (chat, extraData) => {
      if (!chat.assignedTo || !chat.assignedTo.length) return "";
      let doc = extraData.doctors.find((d) => d.id === chat.assignedTo?.[0]);
      if (doc) {
        return doc.lastName + ", " + doc.firstName;
      }
      return;
    },
  },
  address: {
    type: "text",
    header: i18n.t("tableView.columns.officeAddress"),
    getValue: (chat, extraData) => {
      let doc = extraData.doctors.find((d) => d.id === chat.assignedTo?.[0]);
      if (doc) {
        return doc.address;
      }
      return "";
    },
  },
  zip: {
    type: "text",
    header: i18n.t("tableView.columns.officeZip"),
    getValue: (chat, extraData) => {
      let doc = extraData.doctors.find((d) => d.id === chat.assignedTo?.[0]);
      if (doc) {
        return doc.zip;
      }
      return "";
    },
  },
  state: {
    type: "text",
    header: i18n.t("tableView.columns.officeState"),
    getValue: (chat, extraData) => {
      let doc = extraData.doctors.find((d) => d.id === chat.assignedTo?.[0]);
      if (doc) {
        return doc.state;
      }
      return "";
    },
  },
  createdOn: {
    key: "createdOn",
    type: "datetime",
    editable: false,
    header: i18n.t("tableView.columns.createdOn"),
  },
  patientInsuredName: {
    type: "text",
    header: i18n.t("tableView.columns.insuredName"),
    getValue: (chat) => {
      return chat.user?.custom?.insuredName;
    },
    onEdit: async (chat, value, id, updateEncounter) => {
      await updatePatientVariable(chat, "insuredName", value);
    },
  },
  patientInsuredDob: {
    type: "date",
    header: i18n.t("tableView.columns.insuredDob"),
    key: "user.custom.insuredDob",
    getValue: (chat) => {
      return chat.user?.custom?.insuredDob;
    },
    onEdit: async (chat, value, id, updateEncounter) => {
      await updatePatientVariable(chat, "insuredDob", value);
    },
  },
  patientStreetAddress: {
    type: "text",
    header: i18n.t("tableView.columns.insuredStreetAddress"),
    key: "user.custom.streetAddress",
    getValue: (chat) => {
      return chat.user?.custom?.streetAddress;
    },
    onEdit: async (chat, value, id, updateEncounter) => {
      await updatePatientVariable(chat, "streetAddress", value);
    },
  },
  patientCity: {
    type: "text",
    header: i18n.t("tableView.columns.insuredCity"),
    key: "user.custom.city",
    getValue: (chat) => {
      return chat.user?.custom?.city;
    },
    onEdit: async (chat, value, id, updateEncounter) => {
      await updatePatientVariable(chat, "city", value);
    },
  },
  patientState: {
    type: "text",
    header: i18n.t("tableView.columns.insuredState"),
    key: "user.custom.state",
    getValue: (chat) => {
      return chat.user?.custom?.state;
    },
    onEdit: async (chat, value, id, updateEncounter) => {
      await updatePatientVariable(chat, "state", value);
    },
  },
  patientZip: {
    type: "text",
    header: i18n.t("tableView.columns.insuredZip"),
    key: "user.custom.zip",
    getValue: (chat) => {
      return chat.user?.custom?.zip;
    },
    onEdit: async (chat, value, id, updateEncounter) => {
      await updatePatientVariable(chat, "zip", value);
    },
  },
  patientOtherInsured: {
    type: "text",
    header: i18n.t("tableView.columns.otherInsured"),
    key: "user.custom.otherInsured",
    getValue: (chat) => {
      return chat.user?.custom?.otherInsured;
    },
    onEdit: async (chat, value, id, updateEncounter) => {
      await updatePatientVariable(chat, "otherInsured", value);
    },
  },
  patientOtherInsuredPolicyGroup: {
    type: "text",
    header: i18n.t("tableView.columns.otherInsuredPolicyGroup"),
    key: "user.custom.otherInsuredPolicyGroup",
    getValue: (chat) => {
      return chat.user?.custom?.otherInsuredPolicyGroup;
    },
    onEdit: async (chat, value, id, updateEncounter) => {
      await updatePatientVariable(chat, "otherInsuredPolicyGroup", value);
    },
  },
  patientOtherInsuredPlanName: {
    type: "text",
    header: i18n.t("tableView.columns.otherInsuredPlanName"),
    key: "user.custom.otherInsuredPlanName",
    getValue: (chat) => {
      return chat.user?.custom?.otherInsuredPlanName;
    },
    onEdit: async (chat, value, id, updateEncounter) => {
      await updatePatientVariable(chat, "otherInsuredPlanName", value);
    },
  },

  dateOfCurrentIllnessInjury: {
    key: "contextVariables.local:dateOfCurrentIllnessInjury",
    type: "date",
    header: i18n.t("tableView.columns.dateOfCurrentIllnessInjury"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:dateOfCurrentIllnessInjury", value);
    },
  },
  similarIllnessDate: {
    key: "contextVariables.local:similarIllnessDate",
    type: "date",
    header: i18n.t("tableView.columns.similarIllnessDate"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:similarIllnessDate", value);
    },
  },
  datesUnableToWorkFrom: {
    key: "contextVariables.local:datesUnableToWorkFrom",
    type: "date",
    header: i18n.t("tableView.columns.datesUnableToWorkFrom"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:datesUnableToWorkFrom", value);
    },
  },
  datesUnableToWorkTo: {
    key: "contextVariables.local:datesUnableToWorkTo",
    type: "date",
    header: i18n.t("tableView.columns.datesUnableToWorkTo"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:datesUnableToWorkTo", value);
    },
  },
  hospitalizationFrom: {
    key: "contextVariables.local:hospitalizationFrom",
    type: "date",
    header: i18n.t("tableView.columns.hospitalizationFrom"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:hospitalizationFrom", value);
    },
  },
  hospitalizationTo: {
    key: "contextVariables.local:hospitalizationTo",
    type: "date",
    header: i18n.t("tableView.columns.hospitalizationTo"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:hospitalizationTo", value);
    },
  },
  additionalClaimInformation: {
    key: "contextVariables.local:additionalClaimInformation",
    type: "textarea",
    header: i18n.t("tableView.columns.additionalClaimInformation"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:additionalClaimInformation", value);
    },
  },
  outsideLabCharges: {
    key: "contextVariables.local:outsideLabCharges",
    type: "currency",
    header: i18n.t("tableView.columns.outsideLabCharges"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:outsideLabCharges", value);
    },
  },
  medicaidOtherResubmissionCode: {
    key: "contextVariables.local:medicaidOtherResubmissionCode",
    type: "text",
    header: i18n.t("tableView.columns.medicaidOtherResubmissionCode"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:medicaidOtherResubmissionCode", value);
    },
  },
  originalReferenceNumber: {
    key: "contextVariables.local:originalReferenceNumber",
    type: "text",
    header: i18n.t("tableView.columns.originalReferenceNumber"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:originalReferenceNumber", value);
    },
  },
  priorAuthorizationNumber: {
    key: "contextVariables.local:priorAuthorizationNumber",
    type: "text",
    header: i18n.t("tableView.columns.priorAuthorizationNumber"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:priorAuthorizationNumber", value);
    },
  },

  visitTime: {
    key: "contextVariables.local:visitTime",
    type: "datetime",
    header: i18n.t("tableView.columns.visitTime"),
    getValue: (chat) => {
      return chat.contextVariables?.["local:visitTime"] || chat.createdOn;
    },
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:visitTime", value);
    },
  },
  lastUpdate: {
    key: "lastUpdate",
    type: "datetime",
    header: i18n.t("tableView.columns.lastUpdate"),
  },
  firstName: {
    key: "user.firstName",
    type: "text",
    header: i18n.t("tableView.columns.patientFirstName"),
  },
  lastName: {
    key: "user.lastName",
    type: "text",
    header: i18n.t("tableView.columns.patientLastName"),
  },
  dob: {
    key: "user.dob",
    type: "text",
    header: i18n.t("tableView.columns.dob"),
  },
  gender: {
    key: "user.custom.gender",
    type: "gender",
    header: i18n.t("tableView.columns.gender"),
    template: (value) => {
      return value && <div className={"badge gender-" + value.toLowerCase()}>{value.toUpperCase()}</div>;
    },
  },
  userAge: {
    key: "contextVariables.local:userAge",
    type: "text",
    header: i18n.t("tableView.columns.age"),
  },
  encounterType: {
    key: "contextVariables.local:encounterType",
    type: "encounterType",
    header: i18n.t("tableView.columns.encounterType"),
    template: (value, chat, colData, extraData) => {
      return (
        value && (
          <div className={"badge"}>
            {extraData.noteTypesMap[value]?.label || (value ? value + " " + i18n.t("tableView.options.noteTypeNotFound") : "")}
          </div>
        )
      );
    },
  },
  status: {
    key: "status",
    type: "status",
    header: i18n.t("tableView.columns.noteStatus"),
    template: (value) => {
      let label = "";
      if (value === "completed") {
        label = i18n.t("tableView.statuses.aiGenerated");
      } else if (value === "archived") {
        label = i18n.t("tableView.statuses.signed");
      } else if (value === "active") {
        label = i18n.t("tableView.statuses.draft");
      } else if (value === "invalid") {
        label = i18n.t("tableView.statuses.canceled");
      } else {
        label = value.charAt(0).toUpperCase() + value.slice(1);
      }
      return label && <div className={"badge status-" + value}>{label}</div>;
    },
  },

  insuranceCarrier: {
    key: "contextVariables.local:insuranceCarrier",
    type: "text",
    header: i18n.t("tableView.columns.insuranceCarrier"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:insuranceCarrier", value);
    },
  },
  policyNumber: {
    key: "contextVariables.local:policyNumber",
    type: "text",
    header: i18n.t("tableView.columns.policyNumber"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:policyNumber", value);
    },
  },
  groupNumber: {
    key: "contextVariables.local:groupNumber",
    type: "text",
    header: i18n.t("tableView.columns.groupNumber"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:groupNumber", value);
    },
  },
  sponsorInfo: {
    key: "contextVariables.local:sponsorInfo",
    type: "text",
    header: i18n.t("tableView.columns.sponsorInfo"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:sponsorInfo", value);
    },
  },
  noteReviewed: {
    key: "contextVariables.local:noteReviewed",
    type: "select",
    header: i18n.t("tableView.columns.noteReviewed"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:noteReviewed", value);
    },
    getOptions: () => {
      return noteReviewedOptions;
    },
    getValue: (chat) => {
      let value = chat.contextVariables?.["local:noteReviewed"];
      if (!value) return "";
      let find = noteReviewedOptions.find((item) => item.key === value);
      if (find) return find.label;
    },
  },
  verificationStatus: {
    key: "contextVariables.local:verificationStatus",
    type: "select",
    header: i18n.t("tableView.columns.verificationStatus"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:verificationStatus", value);
    },
    getOptions: () => {
      return verificationStatusOptions;
    },
    getValue: (chat) => {
      let value = chat.contextVariables?.["local:verificationStatus"];
      if (!value) return "";
      let find = verificationStatusOptions.find((item) => item.key === value);
      if (find) return find.label;
    },
  },
  verificationNotes: {
    key: "contextVariables.local:verificationNotes",
    type: "text",
    header: i18n.t("tableView.columns.verificationNotes"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:verificationNotes", value);
    },
  },
  verificationFinalStatus: {
    key: "contextVariables.local:verificationFinalStatus",
    type: "select",
    header: i18n.t("tableView.columns.verificationFinalStatus"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:verificationFinalStatus", value);
    },
    getOptions: () => {
      return verificationFinalStatusOptions;
    },
    getValue: (chat) => {
      let value = chat.contextVariables?.["local:verificationFinalStatus"];
      if (!value) return "";
      let find = verificationFinalStatusOptions.find((item) => item.key === value);
      if (find) return find.label;
    },
  },
  dateVerifiedProcessCompleted: {
    key: "contextVariables.local:dateVerifiedProcessCompleted",
    type: "date",
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:dateVerifiedProcessCompleted", value);
    },
  },
  billedUnitsUsed: {
    editable: false,
    header: i18n.t("tableView.columns.billedUnitsUsed"),
    getValue: (chat) => {
      let val = Object.values(chat.contextVariables?.["local:cpt2Wounds"] || {}).reduce((acc, item) => {
        return acc + (item?.area || 0);
      }, 0);
      return val;
    },
  },
  billedUnitsWasted: {
    editable: false,
    header: i18n.t("tableView.columns.billedUnitsWasted"),
    getValue: (chat) => {
      return Object.values(chat.contextVariables?.["local:cpt2Wounds"] || {}).reduce((acc, item) => {
        return acc + (item?.areaWasted || 0);
      }, 0);
    },
  },
  authRemainedUnits: {
    key: "contextVariables.local:authRemainedUnits",
    type: "integer",
    header: i18n.t("tableView.columns.authRemainedUnits"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:authRemainedUnits", value);
    },
  },
  dateOfService: {
    key: "contextVariables.local:dateOfService",
    type: "date",
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:dateOfService", value, id);
    },
  },
  dateNoteReceived: {
    key: "contextVariables.local:dateNoteReceived",
    type: "date",
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:dateNoteReceived", value);
    },
  },
  dateBilled: {
    key: "contextVariables.local:dateBilled",
    type: "date",
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:dateBilled", value);
    },
  },
  dateReceived: {
    key: "contextVariables.local:dateReceived",
    type: "date",
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:dateReceived", value);
    },
  },
  amountBilled: {
    key: "contextVariables.local:amountBilled",
    type: "currency",
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:amountBilled", value);
    },
  },
  eftDate: {
    key: "contextVariables.local:eftDate",
    type: "date",
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:eftDate", value);
    },
  },
  amountPaid: {
    key: "contextVariables.local:amountPaid",
    type: "currency",
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:amountPaid", value);
    },
  },
  unitsPaid: {
    key: "contextVariables.local:unitsPaid",
    type: "integer",
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:unitsPaid", value);
    },
  },
  claimNumber: {
    key: "contextVariables.local:claimNumber",
    type: "text",
    header: i18n.t("tableView.columns.claimNumber"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:claimNumber", value);
    },
  },
  eftNumber: {
    key: "contextVariables.local:eftNumber",
    type: "text",
    header: i18n.t("tableView.columns.eftNumber"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:eftNumber", value);
    },
  },
  finalStatus: {
    key: "contextVariables.local:finalStatus",
    type: "select",
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:finalStatus", value);
    },
    getOptions: () => {
      return finalStatusOptions;
    },
    getValue: (chat) => {
      let value = chat.contextVariables?.["local:finalStatus"];
      if (!value) return "";
      let find = finalStatusOptions.find((item) => item.key === value);
      if (find) return find.label;
    },
  },
  finalStatusNote: {
    key: "contextVariables.local:finalStatusNote",
    type: "select",
    header: i18n.t("tableView.columns.finalStatusNote"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:finalStatusNote", value);
    },
    getOptions: () => {
      return finalStatusNoteOptions;
    },
    getValue: (chat) => {
      let value = chat.contextVariables?.["local:finalStatusNote"];
      if (!value) return "";
      let find = finalStatusNoteOptions.find((item) => item.key === value);
      if (find) return find.label;
    },
  },
  statusActionDate: {
    key: "contextVariables.local:statusActionDate",
    type: "date",
    header: i18n.t("tableView.columns.statusActionDate"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:statusActionDate", value);
    },
  },
  projectAdjudicationDate: {
    key: "contextVariables.local:projectAdjudicationDate",
    type: "date",
    header: i18n.t("tableView.columns.projectAdjudicationDate"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:projectAdjudicationDate", value);
    },
  },
  eobAttached: {
    key: "contextVariables.local:eobAttached",
    type: "text",
    header: i18n.t("tableView.columns.eobAttached"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:eobAttached", value);
    },
  },
  resolution: {
    key: "contextVariables.local:resolution",
    type: "text",
    header: i18n.t("tableView.columns.resolution"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:resolution", value);
    },
  },
  denialReason: {
    key: "contextVariables.local:denialReason",
    type: "text",
    header: i18n.t("tableView.columns.denialReason"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:denialReason", value);
    },
  },
  appealLevel: {
    key: "contextVariables.local:appealLevel",
    type: "text",
    header: i18n.t("tableView.columns.appealLevel"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:appealLevel", value);
    },
  },
  appealDeadline: {
    key: "contextVariables.local:appealDeadline",
    type: "date",
    header: i18n.t("tableView.columns.appealDeadline"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:appealDeadline", value);
    },
  },
  previousTreatments: {
    key: "contextVariables.local:previousTreatments",
    type: "text",
    header: i18n.t("tableView.columns.previousTreatments"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:previousTreatments", value);
    },
  },
  productPrice: {
    key: "contextVariables.local:productPrice",
    type: "currency",
    header: i18n.t("tableView.columns.productPrice"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:productPrice", value);
    },
  },
  invoiceNumber: {
    key: "contextVariables.local:invoiceNumber",
    type: "text",
    header: i18n.t("tableView.columns.invoiceNumber"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:invoiceNumber", value);
    },
  },
  invoiceAmount: {
    key: "contextVariables.local:invoiceAmount",
    type: "currency",
    header: i18n.t("tableView.columns.invoiceAmount"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:invoiceAmount", value);
    },
  },
  invoiceAmountPaid: {
    key: "contextVariables.local:invoiceAmountPaid",
    type: "currency",
    header: i18n.t("tableView.columns.invoiceAmountPaid"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:invoiceAmountPaid", value);
    },
  },
  miscCharge: {
    key: "contextVariables.local:miscCharge",
    type: "currency",
    header: i18n.t("tableView.columns.miscCharge"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:miscCharge", value);
    },
  },
  netAmount: {
    key: "contextVariables.local:netAmount",
    type: "currency",
    header: i18n.t("tableView.columns.netAmount"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:netAmount", value);
    },
  },
  amoutOtstanding: {
    key: "contextVariables.local:amoutOtstanding",
    type: "currency",
    header: i18n.t("tableView.columns.amountOutstanding"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:amoutOtstanding", value);
    },
  },
  dateAppealSent: {
    key: "contextVariables.local:dateAppealSent",
    type: "date",
    header: i18n.t("tableView.columns.dateAppealSent"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:dateAppealSent", value);
    },
  },
  paymentDate: {
    key: "contextVariables.local:paymentDate",
    type: "date",
    header: i18n.t("tableView.columns.paymentDate"),
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:paymentDate", value);
    },
  },

  // WOUND INFORMATION

  woundOnsetDate: {
    key: "contextVariables.local:woundOnsetDate",
    type: "date",
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:woundOnsetDate", value, id);
    },
  },
  prevTxDetailsCompleted: {
    key: "contextVariables.local:prevTxDetailsCompleted",
    type: "select",
    getOptions: () => [
      { key: "clinicalToWork", label: i18n.t("tableView.options.clinicalToWork") },
      { key: "clinicalToComplete", label: i18n.t("tableView.options.clinicalToComplete") },
      { key: "Unavailable", label: i18n.t("tableView.options.unavailable") },
    ],
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:prevTxDetailsCompleted", value, id);
    },
  },
  patientTreatmentLocation: {
    key: "contextVariables.local:patientTreatmentLocation",
    type: "select",
    getOptions: () => [
      { key: "nursingHome", label: i18n.t("tableView.options.nursingHome") },
      { key: "skilledNursingFacility", label: i18n.t("tableView.options.skilledNursingFacility") },
      { key: "office", label: i18n.t("tableView.options.office") },
      { key: "home", label: i18n.t("tableView.options.home") },
      { key: "assistedLiving", label: i18n.t("tableView.options.assistedLiving") },
    ],
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:patientTreatmentLocation", value, id);
    },
  },
  woundImagesAvailable: {
    key: "contextVariables.local:woundImagesAvailable",
    type: "select",
    header: i18n.t("tableView.columns.woundImagesAvailable"),
    getOptions: () => [
      { key: "yes", label: i18n.t("tableView.options.yes") },
      { key: "down", label: i18n.t("tableView.options.down") },
    ],
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:woundImagesAvailable", value, id);
    },
  },
  woundClassification: {
    key: "contextVariables.local:woundClassification",
    type: "select",
    header: i18n.t("tableView.columns.woundClassification"),
    getOptions: () => [
      { key: "dfu", label: i18n.t("tableView.woundClassifications.dfu") },
      { key: "vlu", label: i18n.t("tableView.woundClassifications.vlu") },
      { key: "arterial", label: i18n.t("tableView.woundClassifications.arterial") },
      { key: "pressure", label: i18n.t("tableView.woundClassifications.pressure") },
      { key: "nonPresure", label: i18n.t("tableView.woundClassifications.nonPressure") },
      { key: "surgical", label: i18n.t("tableView.woundClassifications.surgical") },
      { key: "trauma", label: i18n.t("tableView.woundClassifications.trauma") },
      { key: "other", label: i18n.t("tableView.woundClassifications.other") },
    ],
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:woundClassification", value, id);
    },
  },
  woundSiteLocation: {
    key: "contextVariables.local:woundSiteLocation",
    type: "select",
    header: i18n.t("tableView.columns.woundSiteLocation"),
    getOptions: () => [
      { key: "lowerExtremity", label: i18n.t("tableView.woundLocations.lowerExtremity") },
      { key: "upperExtremity", label: i18n.t("tableView.woundLocations.upperExtremity") },
      { key: "sacrum", label: i18n.t("tableView.woundLocations.sacrum") },
      { key: "buttocks", label: i18n.t("tableView.woundLocations.buttocks") },
      { key: "trunk", label: i18n.t("tableView.woundLocations.trunk") },
      { key: "head", label: i18n.t("tableView.woundLocations.head") },
      { key: "foot", label: i18n.t("tableView.woundLocations.foot") },
      { key: "ischium", label: i18n.t("tableView.woundLocations.ischium") },
    ],
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:woundSiteLocation", value, id);
    },
  },
  totalSurfaceArea: {
    key: "contextVariables.local:totalSurfaceArea",
    type: "decimal",
    header: i18n.t("tableView.columns.totalSurfaceArea"),
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:totalSurfaceArea", value, id);
    },
  },
  totalAreaWithDepth: {
    key: "contextVariables.local:totalAreaWithDepth",
    type: "decimal",
    header: i18n.t("tableView.columns.totalAreaWithDepth"),
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:totalAreaWithDepth", value, id);
    },
  },
  percentImprovement: {
    key: "contextVariables.local:percentImprovement",
    type: "decimal",
    header: i18n.t("tableView.columns.percentImprovement"),
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:percentImprovement", value, id);
    },
  },
  productApplicationDateOfService: {
    key: "contextVariables.local:productApplicationDateOfService",
    type: "date",
    header: i18n.t("tableView.columns.productApplicationDateOfService"),
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:productApplicationDateOfService", value, id);
    },
  },
  trackingNumber: {
    key: "contextVariables.local:trackingNumber",
    type: "text",
    header: i18n.t("tableView.columns.trackingNumber"),
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:trackingNumber", value, id);
    },
  },

  // Note information

  hpi: {
    key: "contextVariables.local:hpi",
    type: "textarea",
    header: i18n.t("tableView.columns.hpi"),
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:hpi", value, id);
    },
  },
  pastMedicalHistory: {
    key: "contextVariables.local:pastMedicalHistory",
    type: "textarea",
    header: i18n.t("tableView.columns.pastMedicalHistory"),
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:pastMedicalHistory", value, id);
    },
  },
  pastSurgeries: {
    key: "contextVariables.local:pastSurgeries",
    type: "textarea",
    header: i18n.t("tableView.columns.pastSurgeries"),
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:pastSurgeries", value, id);
    },
  },
  allergies: {
    key: "contextVariables.local:allergies",
    type: "textarea",
    header: i18n.t("tableView.columns.allergies"),
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:allergies", value, id);
    },
  },
  currentMedications: {
    key: "contextVariables.local:currentMedications",
    type: "textarea",
    header: i18n.t("tableView.columns.currentMedications"),
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:currentMedications", value, id);
    },
  },
  familyHistory: {
    key: "contextVariables.local:familyHistory",
    type: "textarea",
    header: i18n.t("tableView.columns.familyHistory"),
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:familyHistory", value, id);
    },
  },
  socialHistory: {
    key: "contextVariables.local:socialHistory",
    type: "textarea",
    header: i18n.t("tableView.columns.socialHistory"),
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:socialHistory", value, id);
    },
  },
  ros: {
    key: "contextVariables.local:ros",
    type: "textarea",
    header: i18n.t("tableView.columns.ros"),
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:ros", value, id);
    },
  },
  physicalExams: {
    key: "contextVariables.local:physicalExams",
    type: "textarea",
    header: i18n.t("tableView.columns.physicalExams"),
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:physicalExams", value, id);
    },
  },
  totalWounds: {
    key: "contextVariables.local:totalWounds",
    type: "integer",
    header: i18n.t("tableView.columns.totalWounds"),
  },
  diagnostics: {
    key: "contextVariables.local:diagnostics",
    type: "textarea",
    header: i18n.t("tableView.columns.diagnostics"),
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:diagnostics", value, id);
    },
  },
  uploadedTot_labs: {
    key: "contextVariables.local:uploadedTot_labs",
    type: "integer",
    header: i18n.t("tableView.columns.uploadedTotalLabs"),
  },
  planOfCare: {
    key: "contextVariables.local:planOfCare",
    type: "textarea",
    header: i18n.t("tableView.columns.planOfCare"),
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:planOfCare", value, id);
    },
  },
  inOfficeProcedures: {
    key: "contextVariables.local:inOfficeProcedures",
    type: "array",
    header: i18n.t("tableView.columns.officeProcedures"),
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:inOfficeProcedures", value, id);
    },
    getValue: (chat) => {
      let val = chat.contextVariables?.["local:inOfficeProcedures"];
      if (!val || !val.length) return 0;
      if (val === "No bedside procedures were performed.") {
        return 0;
      }
      if (val === "<p>No in-office procedure performed during this visit</p>") {
        return 0;
      }
      if (typeof val === "string") {
        return 1;
      }
      if (Array.isArray(val)) {
        return val.length;
      }

      return 0;
    },
    updateTemplate: (chat, value, setValue, renderMic) => {
      return (
        <TableViewICD10
          chat={chat}
          value={value}
          setValue={setValue}
          renderMic={renderMic}
          initValue={chat.contextVariables?.["local:inOfficeProcedures"]}
          showAdd={true}
          type="inOfficeProcedures"
        ></TableViewICD10>
      );
    },
  },
  nonBillableInjections: {
    key: "contextVariables.local:nonBillableInjections",
    type: "array",
    header: i18n.t("tableView.columns.nonBillableInjections"),
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:nonBillableInjections", stampNonBillable(value), id);
    },
    getValue: (chat) => {
      const val = chat.contextVariables?.["local:nonBillableInjections"];
      if (!val || !val.length) return 0;
      if (typeof val === "string") return 1;
      if (Array.isArray(val)) return val.length;
      return 0;
    },
    updateTemplate: (chat, value, setValue, renderMic) => {
      return (
        <TableViewICD10
          chat={chat}
          value={value}
          setValue={setValue}
          renderMic={renderMic}
          initValue={chat.contextVariables?.["local:nonBillableInjections"]}
          showAdd={true}
          type="nonBillableInjections"
        ></TableViewICD10>
      );
    },
  },
  cpt2codes: {
    key: "contextVariables.local:cpt2codes",
    type: "array",
    header: i18n.t("tableView.columns.cptLevelII"),
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:cpt2codes", value, id);
    },
    getValue: (chat) => {
      let val = chat.contextVariables?.["local:cpt2codes"];

      if (!val || !val.length) return 0;
      if (val === "No bedside procedures were performed.") {
        return 0;
      }
      if (val === "<p>No in-office procedure performed during this visit</p>") {
        return 0;
      }
      if (typeof val === "string") {
        return 1;
      }
      if (Array.isArray(val)) {
        return val.length;
      }

      return 0;
    },
    updateTemplate: (chat, value, setValue, renderMic) => {
      return (
        <TableViewICD10
          chat={chat}
          value={value}
          setValue={setValue}
          renderMic={renderMic}
          initValue={chat.contextVariables?.["local:cpt2codes"]}
          showAdd={true}
          type="cpt2codes"
        ></TableViewICD10>
      );
    },
  },
  billableCodesICD10: {
    key: "contextVariables.local:billableCodesICD10",
    type: "array",
    header: i18n.t("tableView.columns.diagnosisCodesICD10"),
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:billableCodesICD10", value, id);
    },
    getValue: (chat) => {
      let val = chat.contextVariables?.["local:billableCodesICD10"];

      if (!val) return 0;
      if (typeof val === "string") {
        return 0;
      }
      if (Array.isArray(val)) {
        return val.length;
      }
      if (typeof val === "object") {
        return Object.keys(val).length;
      }

      return 0;
    },
    updateTemplate: (chat, value, setValue, renderMic) => {
      return (
        <TableViewICD10
          chat={chat}
          value={value}
          setValue={setValue}
          renderMic={renderMic}
          initValue={chat.contextVariables?.["local:billableCodesICD10"]}
          showAdd={true}
          type="billableCodesICD10"
        ></TableViewICD10>
      );
    },
  },
  cpt2Wounds: {
    key: "contextVariables.local:cpt2Wounds",
    type: "string",
    header: i18n.t("tableView.columns.woundProductCode"),
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:cpt2Wounds", value, id);
    },
    // The table view saves through ModalEditTable, never the Q code editor's own
    // Save, so the missing-CPT confirmation has to be asked from here.
    confirmBeforeSave: (value) => confirmMissingCpt(Array.isArray(value) ? value[0] : value, (key) => i18n.t(key)),
    getValue: (chat) => {
      let val = chat.contextVariables?.["local:cpt2Wounds"];
      if (!val) return "";
      if (Array.isArray(val) && val.length > 0) {
        return val[0]?.code || "";
      }
      if (typeof val === "object") {
        return val?.code || "";
      }

      return "";
    },
    updateTemplate: (chat, value, setValue) => {
      return (
        <TableViewQCodes
          chat={chat}
          value={value}
          setValue={setValue}
          initValue={chat.contextVariables?.["local:cpt2Wounds"]}
        ></TableViewQCodes>
      );
    },
  },
  medicalDecisionMaking: {
    key: "contextVariables.local:medicalDecisionMaking",
    type: "textarea",
    header: i18n.t("tableView.columns.mdm"),
    request: true,
    onEdit: async (chat, value, id, updateEncounter) => {
      await updateContextVariable(chat, updateEncounter, "local:medicalDecisionMaking", value, id);
    },
  },
};

const noteReviewedOptions = [
  { key: "yes", label: i18n.t("tableView.statuses.noteReviewedYes") },
  { key: "reviewed", label: i18n.t("tableView.statuses.noteReviewedPreviouslyReviewed") },
  { key: "requested", label: i18n.t("tableView.statuses.noteReviewedRequested") },
  { key: "mgmt", label: i18n.t("tableView.statuses.noteReviewedPerMgmt") },
  { key: "noNote", label: i18n.t("tableView.statuses.noteReviewedNoNote") },
  { key: "providerApplication", label: i18n.t("tableView.statuses.noteReviewedProviderApplication") },
  { key: "providerOrder", label: i18n.t("tableView.statuses.noteReviewedProviderOrder") },
];

const finalStatusOptions = [
  { key: "billed", label: i18n.t("tableView.statuses.billed") },
  { key: "billedCorrectedClaim", label: i18n.t("tableView.statuses.billedCorrectedClaim") },
  { key: "officeBilled", label: i18n.t("tableView.statuses.officeBilled") },
  { key: "paid", label: i18n.t("tableView.statuses.paid") },
  { key: "invoicePaidToHTP", label: i18n.t("tableView.statuses.invoicePaidToHTP") },
  { key: "insPartialPayment", label: i18n.t("tableView.statuses.insPartialPayment") },
  { key: "invoiceReceivedPartialPayment", label: i18n.t("tableView.statuses.invoiceReceivedPartialPayment") },
  { key: "appealNeeded", label: i18n.t("tableView.statuses.appealNeeded") },
  { key: "appealSent", label: i18n.t("tableView.statuses.appealSent") },
  { key: "incorrectPay", label: i18n.t("tableView.statuses.incorrectPay") },
  { key: "reprocessing", label: i18n.t("tableView.statuses.reprocessing") },
  { key: "disapprovedNDC", label: i18n.t("tableView.statuses.disapprovedNDC") },
  { key: "disapprovedMedRecs", label: i18n.t("tableView.statuses.disapprovedMedRecs") },
  { key: "disapprovedMedicalNecessity", label: i18n.t("tableView.statuses.disapprovedMedicalNecessity") },
  { key: "disapprovedCOB", label: i18n.t("tableView.statuses.disapprovedCOB") },
  { key: "disapprovedCoverageTermed", label: i18n.t("tableView.statuses.disapprovedCoverageTermed") },
  { key: "disapprovedNotCovered", label: i18n.t("tableView.statuses.disapprovedNotCovered") },
  { key: "disapproved", label: i18n.t("tableView.statuses.disapproved") },
  { key: "medicalRecordsAudit", label: i18n.t("tableView.statuses.medicalRecordsAudit") },
  { key: "paidRecoupInProcess", label: i18n.t("tableView.statuses.paidRecoupInProcess") },
  { key: "paymentReapplied", label: i18n.t("tableView.statuses.paymentReapplied") },
  { key: "lowPay", label: i18n.t("tableView.statuses.lowPay") },
  { key: "denied", label: i18n.t("tableView.statuses.denied") },
  { key: "unbillable", label: i18n.t("tableView.statuses.unbillable") },
  { key: "invoicePaidToIWC", label: i18n.t("tableView.statuses.invoicePaidToIWC") },
  { key: "partAAtTOS", label: i18n.t("tableView.statuses.partAAtTOS") },
  { key: "insuranceChanged", label: i18n.t("tableView.statuses.insuranceChanged") },
  { key: "twoProductsTwoSuppliersSameDay", label: i18n.t("tableView.statuses.twoProductsTwoSuppliersSameDay") },
  { key: "appealOverturned", label: i18n.t("tableView.statuses.appealOverturned") },
  { key: "specialWorkflow", label: i18n.t("tableView.statuses.specialWorkflow") },
  { key: "rejectedNeverReceivedByCarrier", label: i18n.t("tableView.statuses.rejectedNeverReceivedByCarrier") },
  { key: "billingQuestion", label: i18n.t("tableView.statuses.billingQuestion") },
  { key: "unableToOverturn", label: i18n.t("tableView.statuses.unableToOverturn") },
  { key: "reopeningNeeded", label: i18n.t("tableView.statuses.reopeningNeeded") },
  { key: "reopeningDone", label: i18n.t("tableView.statuses.reopeningDone") },
  { key: "correctedClaimNeeded", label: i18n.t("tableView.statuses.correctedClaimNeeded") },
  { key: "officeDidNotFollowSampleHCFA", label: i18n.t("tableView.statuses.officeDidNotFollowSampleHCFA") },
  { key: "buyAndBill", label: i18n.t("tableView.statuses.buyAndBill") },
  { key: "lowPayOfficeSentBack", label: i18n.t("tableView.statuses.lowPayOfficeSentBack") },
  { key: "appealCheckWithOffice", label: i18n.t("tableView.statuses.appealCheckWithOffice") },
];

const finalStatusNoteOptions = [
  { key: "acceptedForProcessing", label: i18n.t("tableView.statuses.acceptedForProcessing") },
  { key: "claimNotShowing", label: i18n.t("tableView.statuses.claimNotShowing") },
  { key: "noteNotComplete", label: i18n.t("tableView.statuses.noteNotComplete") },
  { key: "requestingRecordsAdditionalInformation", label: i18n.t("tableView.statuses.requestingRecordsAdditionalInformation") },
  { key: "reprocessing", label: i18n.t("tableView.statuses.reprocessing") },
  { key: "medRecsAdditionalInfoSent", label: i18n.t("tableView.statuses.medRecsAdditionalInfoSent") },
  {
    key: "experimentalAndOrInvestigationalReviewingToDetermineIfDenialIsUpheldOrCanBeAppealed",
    label: i18n.t("tableView.statuses.experimentalInvestigationalReview"),
  },
  { key: "denialUpheld", label: i18n.t("tableView.statuses.denialUpheld") },
  { key: "paid", label: i18n.t("tableView.statuses.paid") },
  { key: "paidAppliedToDeductible", label: i18n.t("tableView.statuses.paidAppliedToDeductible") },
  { key: "paidIncorrectlySeeResolutionColumnForDetails", label: i18n.t("tableView.statuses.paidIncorrectlySeeResolution") },
  { key: "paymentAppliedToARecoup", label: i18n.t("tableView.statuses.paymentAppliedToRecoup") },
  { key: "overpayment", label: i18n.t("tableView.statuses.overpayment") },
  { key: "followUpCall", label: i18n.t("tableView.statuses.followUpCall") },
  { key: "newInsuranceCarriersPolicyNotFollowed", label: i18n.t("tableView.statuses.newInsuranceCarriersPolicyNotFollowed") },
  { key: "billingFollowUp", label: i18n.t("tableView.statuses.billingFollowUp") },
  { key: "noAppealPayorPaid1OfThe2Products", label: i18n.t("tableView.statuses.noAppealPayorPaid1Of2") },
  { key: "providerIsOonWithNoPayableBenefits", label: i18n.t("tableView.statuses.providerOonNoPayableBenefits") },
  { key: "unableToObtainBillableInformationFromOffice", label: i18n.t("tableView.statuses.unableToObtainBillableInfo") },
  { key: "officeAppealedWithoutAhmHelp", label: i18n.t("tableView.statuses.officeAppealedWithoutAhmHelp") },
  { key: "appealSent", label: i18n.t("tableView.statuses.appealSent") },
  { key: "officeNotifiedToTakeAction", label: i18n.t("tableView.statuses.officeNotifiedToTakeAction") },
  { key: "incorrectQCodeBilled", label: i18n.t("tableView.statuses.incorrectQCodeBilled") },
  { key: "invoiceRequested", label: i18n.t("tableView.statuses.invoiceRequested") },
  {
    key: "hospiceModifiersMissingOtherHospiceIssueAskChatgpt",
    label: i18n.t("tableView.statuses.hospiceModifiersMissing"),
  },
  { key: "requiredLoopSegmentDetailMissing", label: i18n.t("tableView.statuses.requiredLoopSegmentDetailMissing") },
  { key: "perOfficeTheyWillHandleInternally", label: i18n.t("tableView.statuses.perOfficeHandleInternally") },
  { key: "confirmIfOfficeWantsHelpAskChatgpt", label: i18n.t("tableView.statuses.confirmIfOfficeWantsHelp") },
];

const verificationFinalStatusOptions = [
  { key: "selectAll", label: i18n.t("tableView.verificationStatuses.selectAll") },
  { key: "pendingInfo", label: i18n.t("tableView.verificationStatuses.pendingInfo") },
  { key: "verificationInProcess", label: i18n.t("tableView.verificationStatuses.verificationInProcess") },
  { key: "verificationCompleted", label: i18n.t("tableView.verificationStatuses.verificationCompleted") },
  { key: "authPredetermInProcess", label: i18n.t("tableView.verificationStatuses.authPredetermInProcess") },
  { key: "authPredetermCompleted", label: i18n.t("tableView.verificationStatuses.authPredetermCompleted") },
  { key: "authPredetermNeeded", label: i18n.t("tableView.verificationStatuses.authPredetermNeeded") },
  { key: "appealAuthInProcess", label: i18n.t("tableView.verificationStatuses.appealAuthInProcess") },
  { key: "authExtensionRequested", label: i18n.t("tableView.verificationStatuses.authExtensionRequested") },
  { key: "lessThan100DaysNF", label: i18n.t("tableView.verificationStatuses.lessThan100DaysNF") },
  { key: "managementApprovedDoctorAware", label: i18n.t("tableView.verificationStatuses.managementApprovedDoctorAware") },
  { key: "30DaysConservativeCare", label: i18n.t("tableView.verificationStatuses.thirtyDaysConservativeCare") },
  { key: "90DaysConservativeCare", label: i18n.t("tableView.verificationStatuses.ninetyDaysConservativeCare") },
  { key: "insuranceRequestedInfo", label: i18n.t("tableView.verificationStatuses.insuranceRequestedInfo") },
  { key: "woundInfectedFollowUp", label: i18n.t("tableView.verificationStatuses.woundInfectedFollowUp") },
  { key: "boxSaleOnly", label: i18n.t("tableView.verificationStatuses.boxSaleOnly") },
  { key: "foundNoVerification", label: i18n.t("tableView.verificationStatuses.foundNoVerification") },
  { key: "graftNoLongerNeeded", label: i18n.t("tableView.verificationStatuses.graftNoLongerNeeded") },
  { key: "specialWorkflowNoDoc", label: i18n.t("tableView.verificationStatuses.specialWorkflowNoDoc") },
  { key: "compliancyReviewUnable", label: i18n.t("tableView.verificationStatuses.compliancyReviewUnable") },
  { key: "shipPerManagement", label: i18n.t("tableView.verificationStatuses.shipPerManagement") },
];

const verificationStatusOptions = [
  { key: "pendingInfo", label: i18n.t("tableView.verificationStatuses.pendingInfo") },
  { key: "verificationInProcess", label: i18n.t("tableView.verificationStatuses.verificationInProcess") },
  { key: "verificationCompleted", label: i18n.t("tableView.verificationStatuses.verificationCompleted") },
  { key: "authPredetermInProcess", label: i18n.t("tableView.verificationStatuses.authPredetermInProcess") },
  { key: "authPredetermCompleted", label: i18n.t("tableView.verificationStatuses.authPredetermCompleted") },
  { key: "authPredetermNeeded", label: i18n.t("tableView.verificationStatuses.authPredetermNeeded") },
  { key: "appealAuthInProcess", label: i18n.t("tableView.verificationStatuses.appealAuthInProcess") },
  { key: "authExtensionRequested", label: i18n.t("tableView.verificationStatuses.authExtensionRequested") },
  { key: "lessThan100DaysNF", label: i18n.t("tableView.verificationStatuses.lessThan100DaysNF") },
  { key: "managementApprovedDoctorAware", label: i18n.t("tableView.verificationStatuses.managementApprovedDoctorAware") },
  { key: "30DaysConservativeCare", label: i18n.t("tableView.verificationStatuses.thirtyDaysConservativeCare") },
  { key: "90DaysConservativeCare", label: i18n.t("tableView.verificationStatuses.ninetyDaysConservativeCare") },
  { key: "insuranceRequestedInfo", label: i18n.t("tableView.verificationStatuses.insuranceRequestedInfo") },
  { key: "woundInfectedFollowUp", label: i18n.t("tableView.verificationStatuses.woundInfectedFollowUp") },
  { key: "boxSaleOnly", label: i18n.t("tableView.verificationStatuses.boxSaleOnly") },
  { key: "foundNoVerification", label: i18n.t("tableView.verificationStatuses.foundNoVerification") },
  { key: "graftNoLongerNeeded", label: i18n.t("tableView.verificationStatuses.graftNoLongerNeeded") },
  { key: "verification", label: i18n.t("tableView.verificationStatuses.verification") },
  { key: "specialWorkflowNoDoc", label: i18n.t("tableView.verificationStatuses.specialWorkflowNoDoc") },
  { key: "compliancyReviewUnable", label: i18n.t("tableView.verificationStatuses.compliancyReviewUnable") },
  { key: "shipPerManagement", label: i18n.t("tableView.verificationStatuses.shipPerManagement") },
];

const colorOptions = [
  { key: "#a875cfff", label: i18n.t("tableView.colors.purple") },
  { key: "#2968e1", label: i18n.t("tableView.colors.blue") },
  { key: "#eec9cf", label: i18n.t("tableView.colors.pink") },
  { key: "#a9e1b9", label: i18n.t("tableView.colors.green") },
  { key: "#e66464ff", label: i18n.t("tableView.colors.red") },
  { key: "#faa938ff", label: i18n.t("tableView.colors.orange") },
  { key: "#fff562ff", label: i18n.t("tableView.colors.yellow") },
];

export const updateContextVariable = async (chat, updateEncounter, variable, value, requestApproval) => {
  let chatUpdate = {
    chatId: chat.id,
    contextVariables: {
      [variable]: value,
    },
  };

  if (requestApproval) {
    // DELIBERATELY UNGATED, and the ownership gate is the wrong instrument here.
    //
    // This is not a write to the encounter: it runs "Note Update - Request
    // Approval" (api.js), which FILES A CHANGE REQUEST against the chart for
    // someone else to approve. The UI says so -- the button reads
    // "tableView.edit.requestChange" rather than "update", and these columns
    // carry `request: true` plus a Request History tab (ModalEditTable). Asking
    // permission to change a colleague's chart is exactly what a non-owner is
    // supposed to do, so routing it through ensureAssignedToMe would mean
    // requesting a change SILENTLY TAKES the chart -- defeating the entire
    // purpose of an approval workflow.
    //
    // A gate WAS added here in round 7 on the claim that this branch was dead
    // code. That claim was wrong: 28 built-in columns in this file forward `id`
    // as `requestApproval` (dateOfService, woundOnsetDate, hpi, ...), dispatched
    // through getCol(col).onEdit(chat, value, col.id, updateEncounter) from
    // TableView and ModalEditTable. It is the live "Request change" flow, and
    // the gate crashed all 28 with "ensureAssignedToMe is not a function".
    // Do not re-add it; if this branch ever needs a permission check, it needs a
    // PERMISSION check, not an ownership transfer.
    const capitalize = (str) => {
      // transorm camel case to normal text
      return str
        .replace(/([A-Z])/g, " $1") // insert space before capital letters
        .replace(/^./, (char) => char.toUpperCase()); // capitalize first letter
    };
    const label = tableViewData[requestApproval]?.header || capitalize(requestApproval);
    await api.updateChatRequest(chat.id, variable, label, value);
  } else {
    await updateEncounter(chatUpdate, false, chat);
  }
};

export const updatePatientVariable = async (chat, variable, value) => {
  let updatedPatient = {
    userId: chat.user.id,
    custom: {
      [variable]: value,
    },
  };

  await api.updatePatient(updatedPatient);
};
